/* eslint-disable react/no-danger */
import React, { useRef, useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { useChat } from './hooks';
import VideoItem from './VideoItem';
import BookItem from './BookItem';
import {
  Container,
  MessagesContainer,
  Message,
  MessageContent,
  AIText,
  MessageTime,
  InputContainer,
  TextArea,
  TextAreaRow,
  SendButton,
  SendSpinner,
  ItemWrap,
  InputWrapper,
  ErrMessage,
  Disclaimer,
  Spinner,
  SpinnerText,
  AnimatedDots,
} from './styles';

const SendIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M7.33336 12.6659V4.9419L3.80472 8.47055C3.54437 8.7309 3.12236 8.7309 2.86201 8.47055C2.60166 8.2102 2.60166 7.78819 2.86201 7.52784L7.52868 2.86117L7.57946 2.8156C7.84131 2.60203 8.22731 2.61709 8.47139 2.86117L13.1381 7.52784C13.3984 7.78819 13.3984 8.2102 13.1381 8.47055C12.8777 8.7309 12.4557 8.7309 12.1953 8.47055L8.6667 4.9419V12.6659C8.6667 13.034 8.36822 13.3325 8.00003 13.3325C7.63184 13.3325 7.33336 13.034 7.33336 12.6659Z"
      fill="#FBFBFB"
    />
  </svg>
);

const Chat = () => {
  const { t } = useTranslation('app');
  const latestRef = useRef(null);
  const inputRef = useRef(null);
  const [inputValue, setInputValue] = useState('');
  const { messages, sendMessage, loading, error } = useChat();

  const handleSendMessage = useCallback(() => {
    const messageText = inputValue.trim();

    if (messageText && !loading) {
      sendMessage(messageText);
      setInputValue('');
    }
  }, [inputValue, loading, sendMessage]);

  const handleKeyPress = useCallback(
    (e) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSendMessage();
      }
    },
    [handleSendMessage]
  );

  const formatTime = useCallback(
    (timestamp) => new Date(timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
    []
  );

  const scrollToBottom = useCallback(() => {
    latestRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  return (
    <Container>
      <MessagesContainer>
        {messages.map(({ id, role, content, posts, books, status, timestamp }, index) => (
          <Message
            key={id}
            ref={index === messages.length - 1 ? latestRef : undefined}
            $isUser={role === 'user'}
            $isError={status === 2}
          >
            <MessageContent $isUser={role === 'user'}>
              {status === 0 && !content ? (
                <Spinner>
                  <SpinnerText>
                    {t('Search Fintool knowledge database')}
                    <AnimatedDots />
                  </SpinnerText>
                </Spinner>
              ) : (
                <>
                  <AIText dangerouslySetInnerHTML={{ __html: content }} $isUser={role === 'user'} />
                  {status === 1 && Boolean(posts?.length) && (
                    <ItemWrap>
                      {posts.map((post) => (
                        <VideoItem key={post.id} {...post} />
                      ))}
                    </ItemWrap>
                  )}
                  {status === 1 && Boolean(books?.length) && (
                    <ItemWrap>
                      {books.map((book) => (
                        <BookItem key={book.id} {...book} />
                      ))}
                    </ItemWrap>
                  )}
                </>
              )}
            </MessageContent>
            <MessageTime $isUser={role === 'user'}>{formatTime(timestamp)}</MessageTime>
          </Message>
        ))}
      </MessagesContainer>
      <InputContainer>
        <InputWrapper>
          <TextAreaRow>
            <TextArea
              ref={inputRef}
              name="message"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleKeyPress}
              placeholder={t('What would you like to know')}
              disabled={loading}
            />
            <SendButton
              type="button"
              onClick={handleSendMessage}
              disabled={!inputValue.trim() || loading}
              title={t('Send')}
              aria-label={t('Send')}
            >
              {loading ? <SendSpinner /> : <SendIcon />}
            </SendButton>
          </TextAreaRow>
        </InputWrapper>
        {error && <ErrMessage message={error} />}
        <Disclaimer>{t('Fintool AI does not offer investment advice')}</Disclaimer>
      </InputContainer>
    </Container>
  );
};

export default Chat;
